import { observer } from 'mobx-react-lite';
import { useResolve } from '../use';
import { TOKEN_AdminAccessStore, CODE_SLOTS, type CodeSlot } from './AdminAccessStore';
import { TOKEN_AdminShellStore } from './AdminShellStore';
import { qrModules } from './qrMatrix';
import { TOKEN_ReportStore } from './ReportStore';

// The admin laptop: the day's run list on the left, the equipment-room TV on the right. Legible
// beats pretty, and on this screen legible is the whole product requirement — six QR codes a phone
// has to read from across a room, each with its slot named underneath so a human can point at one.
//
// The "fifteen minutes ahead" rule (docs/access.md) is admin behaviour, not code: the human picks
// the session and the header says which one is up.

const SLOT_LABEL: Record<CodeSlot, string> = {
  white: 'White court',
  red: 'Red court',
  purple: 'Purple court',
  gold: 'Gold court',
  mover: 'Mover',
  frontdesk: 'Front desk',
};

export const AdminView = observer(function AdminView() {
  const shell = useResolve(TOKEN_AdminShellStore);

  if (shell.error !== undefined) {
    return (
      <p role="alert" className="p-8 text-center text-lg text-rose-300">
        {shell.error}
      </p>
    );
  }
  // No day stored on THIS device. Not an error and not a spinner: a new browser profile, cleared
  // site data, or the second laptop. The store refuses to mint one silently, because a minted id
  // opens clean and looks exactly like a real day with nothing in it — so the human is told what
  // this device does and does not have, and starting a new one is a deliberate press.
  if (shell.needsProject) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-lg text-neutral-300">No grading day is stored on this device.</p>
        <p className="max-w-md text-neutral-500">
          If a day is already running, open it on the laptop that started it — a day lives on the
          device that created it, and this one has no record of it.
        </p>
        <button
          onClick={() => void shell.startNewDay()}
          className="rounded-lg bg-neutral-200 px-5 py-3 font-semibold text-neutral-900"
        >
          Start a new grading day
        </button>
      </div>
    );
  }
  if (shell.projectId === undefined) {
    return <p className="p-8 text-center text-neutral-400">Opening the grading day…</p>;
  }
  return (
    <div className="flex flex-1 flex-col gap-6 p-6 lg:flex-row">
      <div className="flex w-full flex-col gap-4 lg:w-96">
        <RunList />
        <NumberingPanel />
        <TickPanel />
        <Reports />
      </div>
      <Tv />
    </div>
  );
});

// Where the selected session's player numbers come from. Deliberately NOT inside the tick panel
// beside sigma and tolerance: those two are the rotation's knobs and this one is the front desk's,
// and grouping it with them would suggest it changes how players move. It sits under the run list,
// where the session is chosen, because it is a property of the session rather than of the hour.
//
// Read off the fold, so it shows what the HALL is working under. Another admin device flipping it,
// or this one flipping it and the write not landing, both show here as the value not moving.
//
// VISUAL, and unverifiable here: whether two radio-style buttons read as a choice rather than as
// two actions, and whether the consequence sentence is enough for a human who has not read
// docs/roster.md. session-v2/visual-residual.md says how to reach this screen.
const NumberingPanel = observer(function NumberingPanel() {
  const shell = useResolve(TOKEN_AdminShellStore);
  const admin = useResolve(TOKEN_AdminAccessStore);
  const mode = shell.numberingMode;

  if (shell.selectedSessionId === undefined || mode === undefined) return null;

  return (
    <section className="flex flex-col gap-3 rounded-lg bg-neutral-900 p-3 ring-1 ring-neutral-800">
      <header className="text-sm tracking-widest text-neutral-500 uppercase">player numbers</header>
      <div role="radiogroup" aria-label="player numbers" className="flex gap-2">
        <ModeButton
          mode="preassigned"
          current={mode}
          label="pre-allocated"
          busy={admin.busy}
          onPick={() => void shell.setNumberingMode('preassigned')}
        />
        <ModeButton
          mode="sequential"
          current={mode}
          label="sequential"
          busy={admin.busy}
          onPick={() => void shell.setNumberingMode('sequential')}
        />
      </div>
      <p className="text-sm text-neutral-400">
        {mode === 'preassigned'
          ? 'The front desk types the number off the club spreadsheet. A number entered twice is refused.'
          : 'The front desk taps a court and the app hands out the next free number.'}
      </p>
    </section>
  );
});

function ModeButton({
  mode,
  current,
  label,
  busy,
  onPick,
}: {
  mode: 'preassigned' | 'sequential';
  current: 'preassigned' | 'sequential';
  label: string;
  busy: boolean;
  onPick: () => void;
}) {
  const picked = mode === current;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={picked}
      // The one already in force is not a write worth making, and pressing it would put a no-op
      // through the ladder and light the busy flag for nothing.
      disabled={busy || picked}
      onClick={onPick}
      className={`flex-1 rounded-lg px-3 py-2 text-lg font-semibold disabled:opacity-60 ${
        picked ? 'bg-sky-600 text-white' : 'bg-neutral-800 text-neutral-300'
      }`}
    >
      {label}
    </button>
  );
}

// The hour, as the admin watches it: the clock, the two knobs, and the list the movers are walking.
// Everything here is derived from the session fold this laptop has open, so a laptop that opened
// the app at minute 35 shows the same numbers as one that has been running since the whistle.
const TickPanel = observer(function TickPanel() {
  const shell = useResolve(TOKEN_AdminShellStore);
  const admin = useResolve(TOKEN_AdminAccessStore);
  const sigma = shell.sigma;
  const tolerance = shell.tolerance;

  if (shell.selectedSessionId === undefined || sigma === undefined || tolerance === undefined) {
    return null;
  }
  return (
    <section className="flex flex-col gap-3 rounded-lg bg-neutral-900 p-3 ring-1 ring-neutral-800">
      <header className="text-sm tracking-widest text-neutral-500 uppercase">the hour</header>
      <p className="text-lg text-neutral-300">
        {shell.elapsedMinutes === undefined
          ? 'Not started. Ticks run from the moment you press Start, not from the scheduled time.'
          : `${shell.elapsedMinutes} min elapsed` +
            (shell.nextTick === undefined
              ? ' — the last move has run.'
              : ` — next move in ${shell.nextTickInMinutes} min.`)}
      </p>

      {/* Two knobs, both events on the session aggregate rather than local settings, or the tick
          stops being reproducible from the log (docs/rotation-algorithm.md). Sigma comes down when
          nobody is moving; the tolerance comes down when the courts drift apart. */}
      {/* Both minus buttons go dead at their floor rather than sending a write the aggregate is
          going to refuse. The precondition is still the real gate — it has to be, because a peer
          device can move the value underneath this render — but a button that exists only to
          produce an error banner is a worse way to say "that is as low as it goes". Sigma must
          stay above zero, so the floor is the last 0.1 step that clears it; tolerance is a whole
          number of players and at least one (docs/rotation.md, "Balance"). */}
      <Knob
        label="sigma"
        value={sigma.toFixed(1)}
        hint="outlier threshold. Lower moves more players."
        busy={admin.busy}
        atFloor={round1(sigma - 0.1) <= 0}
        onDown={() => void shell.setSigma(round1(sigma - 0.1))}
        onUp={() => void shell.setSigma(round1(sigma + 0.1))}
      />
      <Knob
        label="tolerance"
        value={String(tolerance)}
        hint="how far the fullest court may exceed the emptiest."
        busy={admin.busy}
        atFloor={tolerance <= 1}
        onDown={() => void shell.setTolerance(tolerance - 1)}
        onUp={() => void shell.setTolerance(tolerance + 1)}
      />

      <PendingMovesPanel />
      <MoveListPanel />
    </section>
  );
});

// The forecast: what the next tick would move if it ran on the fold as it stands. This is the
// panel the knobs are FOR — turn sigma down and watch rows appear, up and watch them go, before
// the wake makes any of it real. Reasons are kept (the published event strips them): ↑↓ are
// earned, ⇄ is the headcount rebalance, which sigma does not touch.
const PendingMovesPanel = observer(function PendingMovesPanel() {
  const shell = useResolve(TOKEN_AdminShellStore);
  const pending = shell.pendingMoves;

  return (
    <div className="flex flex-col gap-1 border-t border-neutral-800 pt-2">
      <h3 className="text-sm tracking-widest text-neutral-500 uppercase">
        pending — if the tick ran now
      </h3>
      {pending.length === 0 ? (
        <p className="text-neutral-500">Nobody would move.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {pending.map((move) => (
            <li key={move.player} className="flex items-baseline gap-2 text-neutral-400">
              <span className="w-12 text-right text-xl font-bold tabular-nums">{move.player}</span>
              <span>
                {move.from} → {move.to}
              </span>
              <span className="text-xs text-neutral-600">
                {move.reason === 'earned-up' ? '↑' : move.reason === 'earned-down' ? '↓' : '⇄'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

// What is left of the current move list. The admin's question is "is the walking done", so this
// shows only the rows nobody has actioned yet (the mover screen's own rule, read off the fold) and
// says "all moved" the moment the last one lands. History is the mover's audit grid, not this
// panel's.
const MoveListPanel = observer(function MoveListPanel() {
  const shell = useResolve(TOKEN_AdminShellStore);
  const list = shell.moveList;
  const outstanding = shell.outstandingMoves;

  if (list === undefined) {
    return <p className="text-neutral-500">No move list yet.</p>;
  }
  if (list.moves.length === 0) {
    // "If a court is correctly placed, the right number of moves is zero"
    // (docs/rotation-algorithm.md). Said out loud, so an empty list does not read as a failure.
    return <p className="text-neutral-500">Nobody needed moving.</p>;
  }
  if (outstanding.length === 0) {
    return <p className="text-emerald-400">All moved.</p>;
  }
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-sm tracking-widest text-neutral-500 uppercase">
        {outstanding.length} of {list.moves.length} moves outstanding
      </h3>
      <ul className="flex flex-col gap-1">
        {outstanding.map((move) => (
          <li key={move.player} className="flex items-baseline gap-2 text-neutral-300">
            <span className="w-12 text-right text-xl font-bold tabular-nums">{move.player}</span>
            <span className="text-neutral-500">
              {move.from} → {move.to}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
});

function Knob({
  label,
  value,
  hint,
  busy,
  atFloor,
  onDown,
  onUp,
}: {
  label: string;
  value: string;
  hint: string;
  busy: boolean;
  /** The value is as low as the aggregate will accept, so lowering it further is not offered. */
  atFloor: boolean;
  onDown: () => void;
  onUp: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex-1">
        <span className="text-sm text-neutral-500">{label}</span>
        <span className="ml-2 text-2xl font-bold tabular-nums">{value}</span>
        <span className="block text-xs text-neutral-600">{hint}</span>
      </span>
      <button
        aria-label={`lower ${label}`}
        disabled={busy || atFloor}
        onClick={onDown}
        className="h-12 w-12 rounded-lg bg-neutral-700 text-2xl disabled:opacity-30"
      >
        −
      </button>
      <button
        aria-label={`raise ${label}`}
        disabled={busy}
        onClick={onUp}
        className="h-12 w-12 rounded-lg bg-neutral-700 text-2xl disabled:opacity-30"
      >
        +
      </button>
    </div>
  );
}

// Floating point: 2.5 - 0.1 is 2.4000000000000004, and that value would reach an event payload and
// then a report.
const round1 = (value: number): number => Math.round(value * 10) / 10;

// Schedule the day, then start and stop each session. Both write through the OCC ladder; the state
// machine's preconditions shipped in P1, so a double tap on start is rejected by the aggregate
// rather than counted twice.
const RunList = observer(function RunList() {
  const shell = useResolve(TOKEN_AdminShellStore);
  const admin = useResolve(TOKEN_AdminAccessStore);

  return (
    <section className="flex w-full flex-col gap-4">
      <header className="text-sm tracking-widest text-neutral-500 uppercase">
        grading day {shell.projectId}
      </header>
      {admin.lastError !== undefined && (
        <p role="alert" className="rounded-lg bg-amber-950 p-3 text-amber-100">
          {admin.lastError}
        </p>
      )}
      <button
        disabled={admin.busy}
        onClick={() => void shell.scheduleNextSession()}
        className="rounded-lg bg-neutral-200 p-3 font-semibold text-neutral-900 disabled:opacity-30"
      >
        Schedule a session
      </button>
      <ul className="flex flex-col gap-2">
        {shell.sessions.map((s) => (
          <li
            key={s.sessionId}
            className={`flex flex-col gap-2 rounded-lg p-3 ring-1 ${
              s.sessionId === shell.selectedSessionId
                ? 'bg-neutral-800 ring-neutral-500'
                : 'bg-neutral-900 ring-neutral-800'
            }`}
          >
            <button
              onClick={() => shell.selectSession(s.sessionId)}
              className="text-left text-lg font-semibold"
            >
              {s.name}
            </button>
            <div className="flex gap-2 text-sm">
              <button
                disabled={admin.busy}
                onClick={() => void shell.mintCodes(s.sessionId)}
                className="rounded bg-neutral-700 px-3 py-1 disabled:opacity-30"
              >
                {admin.hasCodes(s.sessionId) ? 'Re-mint codes' : 'Mint codes'}
              </button>
              {/* Only the SELECTED session's fold is open on this device, so only its row can be
                  gated on live status: Start goes dead while it runs, Stop while it does not. The
                  other rows keep both buttons and lean on the aggregate's precondition, which is
                  the real guard either way. */}
              <button
                disabled={
                  admin.busy ||
                  (s.sessionId === shell.selectedSessionId && shell.selectedStatus === 'running')
                }
                onClick={() => void shell.startSession(s.sessionId)}
                className="rounded bg-emerald-700 px-3 py-1 disabled:opacity-30"
              >
                Start
              </button>
              <button
                disabled={
                  admin.busy ||
                  (s.sessionId === shell.selectedSessionId && shell.selectedStatus !== 'running')
                }
                onClick={() => void shell.stopSession(s.sessionId)}
                className="rounded bg-rose-800 px-3 py-1 disabled:opacity-30"
              >
                Stop
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
});

// The handover. One CSV per session, downloaded days after the grading day by the laptop that ran
// it (docs/reporting.md), so this section is written for a screen nobody is in a hurry at: the run
// list again, with a button per row and no selection to make first.
//
// One `void store.downloadSession(id)` per row and nothing else. Building the file and handing it
// to the browser both live in the store, because a view may not touch `document` (INVARIANTS
// rules 6 and 12) and because a button whose behaviour is in the JSX is a button nobody can test.
const Reports = observer(function Reports() {
  const reports = useResolve(TOKEN_ReportStore);

  return (
    <section className="flex flex-col gap-3 rounded-lg bg-neutral-900 p-3 ring-1 ring-neutral-800">
      <header className="text-sm tracking-widest text-neutral-500 uppercase">reports</header>
      {reports.lastError !== undefined && (
        <p role="alert" className="rounded-lg bg-amber-950 p-3 text-amber-100">
          {reports.lastError}
        </p>
      )}
      {/* One line for the whole section rather than a label on each button: the store knows an
          export is running and not which row it belongs to, and putting "Building…" on every row
          would say four files are being built. */}
      {reports.busy && <p className="text-neutral-400">Building the file…</p>}
      {reports.sessions.length === 0 ? (
        <p className="text-neutral-500">Nothing has been scheduled today.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {reports.sessions.map((s) => (
            <li key={s.sessionId} className="flex items-center justify-between gap-2">
              <span className="text-neutral-300">{s.name}</span>
              <button
                disabled={reports.busy}
                onClick={() => void reports.downloadSession(s.sessionId)}
                className="rounded bg-neutral-700 px-3 py-1 text-sm disabled:opacity-30"
              >
                Export CSV
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Said on the screen rather than left for somebody to work out from the file: the division
          column is where the last tick SENT each player, which is not where they played
          (docs/reporting.md). */}
      <p className="text-xs text-neutral-600">
        One row per player who finished, gold division first. The division is where the final tick
        placed them.
      </p>
    </section>
  );
});

// The wall. Six QR codes for the selected session, each encoding the full join URL, each with its
// slot named so the human pointing at the screen and the human holding the phone agree.
const Tv = observer(function Tv() {
  const shell = useResolve(TOKEN_AdminShellStore);
  const admin = useResolve(TOKEN_AdminAccessStore);
  const sessionId = shell.selectedSessionId;
  const projectId = shell.projectId;

  if (sessionId === undefined || projectId === undefined) {
    return <Placeholder>Pick a session to put its codes on the TV.</Placeholder>;
  }
  if (!admin.hasCodes(sessionId)) {
    return <Placeholder>No codes minted for this session yet.</Placeholder>;
  }
  const urls = admin.joinUrls(sessionId, { origin: shell.origin, projectId });

  return (
    <section className="flex flex-1 flex-col gap-4">
      <h2 className="text-center text-2xl font-semibold">
        {shell.selectedSession?.name ?? sessionId}
      </h2>
      <div className="grid flex-1 grid-cols-2 gap-4 xl:grid-cols-3">
        {CODE_SLOTS.map((slot) => (
          <figure
            key={slot}
            className="flex flex-col items-center gap-2 rounded-xl bg-white p-4 text-neutral-900"
          >
            <QrCode url={urls[slot]} label={SLOT_LABEL[slot]} />
            {/* The caption doubles as the join link so a laptop can open a role in a tab.
                _blank on purpose: a plain click must not navigate the admin tab into a role. */}
            <figcaption className="text-lg font-semibold">
              <a href={urls[slot]} target="_blank" rel="noreferrer" className="hover:underline">
                {SLOT_LABEL[slot]}
              </a>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
});

// One dark rect per dark module, scaled by the SVG viewBox so the code fills whatever the TV gives
// it. No canvas, no effect, no state: `qrModules` is synchronous and pure, which is what keeps this
// a plain render (INVARIANTS rule 6).
function QrCode({ url, label }: { url: string; label: string }) {
  const modules = qrModules(url);
  const size = modules.length;
  // The quiet zone the spec asks for. Without it a scanner reading off a screen edge fails.
  const margin = 2;
  const span = size + margin * 2;

  return (
    <svg
      viewBox={`0 0 ${span} ${span}`}
      className="aspect-square w-full max-w-72"
      role="img"
      aria-label={`${label} join code`}
      shapeRendering="crispEdges"
    >
      <rect width={span} height={span} fill="#fff" />
      {modules.map((row, y) =>
        row.map((dark, x) =>
          dark ? (
            <rect key={`${y}-${x}`} x={x + margin} y={y + margin} width={1} height={1} />
          ) : null,
        ),
      )}
    </svg>
  );
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return (
    <section className="flex flex-1 items-center justify-center text-neutral-500">
      {children}
    </section>
  );
}
